<template>
  <div class="hco" :style="vendorVars">
    <!-- ── Vendor-branded header band (themed by the --vendor-* contract only) ── -->
    <header class="hco-head">
      <div class="hco-head__in">
        <div class="hco-brand">
          <span class="hco-brand__mark" aria-hidden="true">
            <img v-if="theme.logoUrl" :src="theme.logoUrl" :alt="theme.name" class="hco-brand__logo" />
            <span v-else class="hco-brand__glyph">{{ brandInitial }}</span>
          </span>
          <span class="hco-brand__name">{{ theme.name }}</span>
        </div>
        <div class="hco-head__meta">
          <span class="hco-secure">
            <svg viewBox="0 0 24 24" class="hco-lock"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
            Secure checkout
          </span>
        </div>
      </div>
    </header>

    <div class="hco-wrap">
      <!-- ══ PENDING — order summary + confirm ══ -->
      <div v-if="state === 'pending'" class="hco-grid">
        <main class="hco-card hco-pad hco-order">
          <h1 class="hco-title">{{ orderTitle }}</h1>
          <p class="hco-sub">{{ orderSubtitle }}</p>

          <div class="hco-sec-h">Order summary</div>
          <div v-for="(line, i) in summary.lines" :key="i" class="hco-line" :class="{ 'hco-line--lead': i === 0 }">
            <div class="hco-line__main">
              <div class="hco-line__name">
                {{ line.name }}
                <span v-if="line.qty > 1" class="hco-qty">×{{ line.qty }}</span>
              </div>
              <div v-if="line.desc" class="hco-line__desc">{{ line.desc }}</div>
            </div>
            <div class="hco-money">{{ line.amount }}<span v-if="i === 0 && summary.interval" class="hco-per">/{{ intervalShort }}</span></div>
          </div>

          <div v-if="summary.trial" class="hco-trial">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
            <span>
              <b>{{ summary.trial.days }}-day free trial.</b>
              {{ summary.dueToday }} due today. First payment of <b>{{ summary.trial.firstCharge }}</b>
              on {{ summary.trial.endDate }}, then {{ intervalWord }}.
            </span>
          </div>

          <div class="hco-total">
            <div>
              <span class="hco-total__lbl">Due today</span>
              <span v-if="summary.trial" class="hco-total__sub">then {{ summary.trial.firstCharge }}{{ summary.interval ? '/' + intervalShort : '' }} after trial</span>
              <span v-if="summary.vat" class="hco-total__sub">{{ summary.trial ? 'Each payment includes' : 'Includes' }} {{ summary.vat.amount }} VAT ({{ summary.vat.rate }}%)</span>
            </div>
            <div class="hco-money hco-total__amt">{{ summary.dueToday }}</div>
          </div>

          <button class="hco-pay" :disabled="!payUrl" @click="goToGateway">
            <svg viewBox="0 0 24 24" class="hco-pay__i"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
            {{ payButtonLabel }}
          </button>
          <p v-if="!payUrl" class="hco-nourl">This checkout link is no longer ready for payment. Please start again from {{ theme.name }}.</p>

          <div class="hco-gw">
            <span>Payments processed by</span>
            <span class="hco-gw__name">{{ gatewayLabel }}</span>
            <span class="hco-dot">·</span>
            <span>Secured by Hermes</span>
          </div>

          <div class="hco-trust">
            <div><svg viewBox="0 0 24 24"><path d="M12 2 4 5v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V5z"/></svg>PCI-DSS compliant</div>
            <div><svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>3-D Secure</div>
            <div v-if="summary.isSubscription"><svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg>Cancel anytime</div>
          </div>
        </main>

        <aside class="hco-card hco-pad hco-aside">
          <div class="hco-sec-h">{{ theme.name }}</div>
          <div class="hco-aside__prod">{{ summary.productName || theme.name }}</div>
          <div v-if="summary.lines[0] && summary.lines[0].desc" class="hco-aside__desc">{{ summary.lines[0].desc }}</div>
          <div class="hco-aside__price">
            <span class="hco-money hco-aside__amt">{{ summary.total }}</span>
            <span v-if="summary.interval" class="hco-per">/{{ intervalShort }}</span>
          </div>
          <span v-if="summary.trial" class="hco-badge hco-badge--trial">
            <span class="hco-badge__dot"></span>Trialing until {{ summary.trial.endDate }}
          </span>
        </aside>
      </div>

      <!-- ══ COMPLETED — success receipt ══ -->
      <div v-else-if="state === 'completed'" class="hco-card hco-pad hco-result">
        <div class="hco-ico hco-ico--ok">
          <svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg>
        </div>
        <h2>You're all set</h2>
        <p>{{ completedMessage }}</p>
        <div class="hco-receipt">
          <div class="hco-r" v-if="summary.productName"><span class="hco-r__k">Product</span><span>{{ summary.productName }}</span></div>
          <div class="hco-r" v-if="summary.trial">
            <span class="hco-r__k">Status</span>
            <span class="hco-badge hco-badge--trial"><span class="hco-badge__dot"></span>Trialing</span>
          </div>
          <div class="hco-r" v-if="summary.trial"><span class="hco-r__k">Trial ends</span><span class="hco-mono">{{ summary.trial.endDate }}</span></div>
          <div class="hco-r"><span class="hco-r__k">{{ summary.trial ? 'First charge' : 'Total' }}</span><span class="hco-money">{{ summary.trial ? summary.trial.firstCharge : summary.total }}</span></div>
        </div>
        <p class="hco-handoff">Return to {{ theme.name }} to finish activating your subscription — your product will pick up the new licence automatically.</p>
        <div class="hco-btns">
          <a v-if="returnUrl" class="hco-btn hco-btn--primary" :href="returnUrl">
            Return to {{ theme.name }}
          </a>
        </div>
      </div>

      <!-- ══ CANCELLED / EXPIRED — honest states ══ -->
      <div v-else class="hco-card hco-pad hco-result">
        <div class="hco-ico hco-ico--neutral">
          <svg v-if="state === 'expired'" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
          <svg v-else viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18"/></svg>
        </div>
        <h2>{{ state === 'expired' ? 'This checkout has expired' : 'Checkout cancelled' }}</h2>
        <p>{{ endStateMessage }}</p>
        <div class="hco-btns">
          <a v-if="returnUrl" class="hco-btn hco-btn--primary" :href="returnUrl">Return to {{ theme.name }}</a>
        </div>
        <p v-if="supportEmail" class="hco-support">Need help? Contact <a :href="`mailto:${supportEmail}`">{{ supportEmail }}</a>.</p>
      </div>
    </div>

    <footer class="hco-foot">
      <span>{{ theme.name }} is billed through <b>{{ gatewayLabel }}</b>, powered by {{ poweredBy }}.</span>
      <span class="hco-foot__sep">·</span>
      <span>Prices in {{ summary.currency }} incl. VAT where applicable.</span>
    </footer>
  </div>
</template>

<script>
// HermesCheckout — the public, vendor-themed, Hermes-hosted checkout island
// (design/surfaces/checkout.html). Rendered on the chrome-less `noUI` shell at
// GET /pay/{token}. Purely presentational: every value arrives as a prop from the
// server (HostedCheckoutViewService); it makes NO API calls, so it works with no
// session. The primary button hands off to the gateway's hosted payment URL —
// this build's Stripe/Revolut adapters expose hosted checkout only (no embedded
// card capture), so an honest order+confirm page is the correct V1.
export default {
  name: 'HermesCheckout',
  props: {
    componentData: { type: Object, default: () => ({}) },
    state:        { type: String, default: 'pending' },
    mode:         { type: String, default: 'subscription' },
    theme:        { type: Object, default: () => ({}) },
    summary:      { type: Object, default: () => ({}) },
    payUrl:       { type: String, default: null },
    gatewayLabel: { type: String, default: 'the payment gateway' },
    supportEmail: { type: String, default: null },
    returnUrl:    { type: String, default: null },
    poweredBy:    { type: String, default: 'Hermes' },
  },
  computed: {
    // Apply only the vendor tokens that are set; unset ones fall through to the
    // Hermes fallback via `var(--vendor-*, var(--h-*))` in the scoped CSS.
    vendorVars() {
      const t = this.theme || {};
      const vars = {};
      if (t.accent)      vars['--vendor-accent'] = t.accent;
      if (t.accentHover) vars['--vendor-accent-hover'] = t.accentHover;
      if (t.onAccent)    vars['--vendor-on-accent'] = t.onAccent;
      if (t.accentTint)  vars['--vendor-accent-tint'] = t.accentTint;
      if (t.surface)     vars['--vendor-surface'] = t.surface;
      if (t.onSurface)   vars['--vendor-on-surface'] = t.onSurface;
      if (t.radius)      vars['--vendor-radius'] = t.radius;
      return vars;
    },
    brandInitial() {
      const n = (this.theme && this.theme.name) || '?';
      return n.trim().charAt(0).toUpperCase();
    },
    intervalShort() {
      return { monthly: 'mo', quarterly: 'qtr', yearly: 'yr', one_time: '' }[this.summary.interval] || 'mo';
    },
    intervalWord() {
      return { monthly: 'monthly', quarterly: 'quarterly', yearly: 'yearly', one_time: 'once' }[this.summary.interval] || 'monthly';
    },
    orderTitle() {
      if (this.summary.trial) return 'Start your free trial';
      return this.summary.isSubscription ? 'Complete your subscription' : 'Complete your purchase';
    },
    orderSubtitle() {
      const name = this.summary.productName || this.theme.name;
      if (this.summary.trial) {
        return `Start your ${this.summary.trial.days}-day free trial of ${name}. You won't be charged until ${this.summary.trial.endDate}.`;
      }
      return `Review your order for ${name} and continue to secure payment.`;
    },
    payButtonLabel() {
      if (this.summary.trial) return 'Start trial';
      return this.summary.isSubscription ? 'Subscribe & pay' : 'Continue to payment';
    },
    completedMessage() {
      if (this.summary.trial) return `Your ${this.summary.productName || this.theme.name} trial is active. A confirmation is on its way.`;
      return `Your payment was received. A confirmation is on its way.`;
    },
    endStateMessage() {
      if (this.state === 'expired') {
        return 'This checkout link has expired and can no longer be paid. No charge was made — start again to get a fresh link.';
      }
      return 'You cancelled before payment completed. No charge was made. You can start over whenever you\'re ready.';
    },
  },
  methods: {
    goToGateway() {
      if (this.payUrl) window.location.assign(this.payUrl);
    },
  },
};
</script>

<style scoped>
.hco {
  min-height: 100vh;
  background: var(--h-bg-content);
  color: var(--h-text-primary);
  font-family: var(--h-font-body);
  display: flex;
  flex-direction: column;
}

/* Header band */
.hco-head { background: var(--vendor-surface, var(--h-bg-card)); border-bottom: 1px solid var(--h-border); }
.hco-head__in { max-width: 1000px; margin: 0 auto; padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.hco-brand { display: flex; align-items: center; gap: 11px; color: var(--vendor-on-surface, var(--h-text-primary)); font-weight: 700; font-size: 18px; letter-spacing: -.01em; }
.hco-brand__mark { width: 34px; height: 34px; flex: none; border-radius: 9px; background: var(--vendor-accent, var(--h-accent)); display: flex; align-items: center; justify-content: center; overflow: hidden; }
.hco-brand__logo { width: 100%; height: 100%; object-fit: contain; }
.hco-brand__glyph { color: var(--vendor-on-accent, #fff); font-weight: 700; font-size: 17px; }
.hco-head__meta { font-size: 12.5px; color: var(--vendor-on-surface, var(--h-text-secondary)); opacity: .85; }
.hco-secure { display: inline-flex; align-items: center; gap: 6px; font-weight: 600; }
.hco-lock { width: 14px; height: 14px; stroke: var(--h-success); fill: none; stroke-width: 2; }

/* Layout */
.hco-wrap { flex: 1; max-width: 1000px; width: 100%; margin: 0 auto; padding: 28px 24px 48px; }
.hco-grid { display: grid; grid-template-columns: 1fr 360px; gap: 24px; align-items: start; }
.hco-card { background: var(--h-bg-card); border: 1px solid var(--h-border); border-radius: var(--vendor-radius, var(--h-radius-lg)); box-shadow: var(--h-shadow-card); }
.hco-pad { padding: 24px; }
.hco-title { font-size: 22px; font-weight: 600; letter-spacing: -.02em; margin: 0 0 4px; }
.hco-sub { color: var(--h-text-secondary); font-size: 14px; margin: 0 0 22px; }
.hco-sec-h { font-size: 12px; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; color: var(--h-text-secondary); margin: 0 0 12px; }

/* Order lines */
.hco-line { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 11px 0; border-bottom: 1px solid var(--h-border); }
.hco-line--lead .hco-line__name { font-weight: 600; font-size: 15px; }
.hco-line__name { font-size: 13.5px; }
.hco-qty { color: var(--h-text-tertiary); font-weight: 500; margin-left: 4px; }
.hco-line__desc { color: var(--h-text-secondary); font-size: 12.5px; margin-top: 3px; }
.hco-money { font-family: var(--h-font-mono); font-variant-numeric: tabular-nums; font-weight: 600; white-space: nowrap; }
.hco-per { font-weight: 400; color: var(--h-text-tertiary); font-size: 11px; }

.hco-trial { margin-top: 16px; background: var(--h-violet-bg); border: 1px solid var(--h-violet-border); color: var(--h-violet-text); border-radius: var(--h-radius); padding: 11px 13px; font-size: 12.5px; display: flex; gap: 9px; }
.hco-trial svg { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2; flex: none; margin-top: 1px; }

.hco-total { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; padding-top: 16px; margin-top: 8px; border-top: 1px solid var(--h-border); }
.hco-total__lbl { font-weight: 600; font-size: 14px; display: block; }
.hco-total__sub { display: block; font-size: 11.5px; color: var(--h-text-tertiary); }
.hco-total__amt { font-size: 20px; }

.hco-pay { width: 100%; margin-top: 20px; font: inherit; font-size: 15px; font-weight: 600; padding: 13px 20px; border-radius: var(--h-radius); border: none; cursor: pointer; background: var(--vendor-accent, var(--h-accent)); color: var(--vendor-on-accent, #fff); display: flex; align-items: center; justify-content: center; gap: 9px; transition: background .13s; }
.hco-pay:hover:not(:disabled) { background: var(--vendor-accent-hover, var(--h-accent-hover)); }
.hco-pay:disabled { opacity: .55; cursor: not-allowed; }
.hco-pay:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.hco-pay__i { width: 18px; height: 18px; stroke: currentColor; fill: none; stroke-width: 2; }
.hco-nourl { margin-top: 12px; font-size: 12.5px; color: var(--h-danger-text); text-align: center; }

.hco-gw { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 8px; margin-top: 14px; padding: 9px; border: 1px solid var(--h-border); background: var(--h-bg-subtle); border-radius: var(--h-radius); font-size: 11.5px; color: var(--h-text-secondary); }
.hco-gw__name { font-weight: 700; color: var(--h-text-primary); }
.hco-dot { color: var(--h-text-tertiary); }
.hco-trust { display: flex; flex-wrap: wrap; gap: 14px 20px; margin-top: 18px; padding-top: 18px; border-top: 1px solid var(--h-border); }
.hco-trust div { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--h-text-secondary); }
.hco-trust svg { width: 15px; height: 15px; stroke: var(--h-success); fill: none; stroke-width: 2; flex: none; }

/* Aside summary */
.hco-aside__prod { font-weight: 600; font-size: 15px; }
.hco-aside__desc { color: var(--h-text-secondary); font-size: 12.5px; margin-top: 4px; }
.hco-aside__price { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--h-border); display: flex; align-items: baseline; gap: 4px; }
.hco-aside__amt { font-size: 20px; }
.hco-badge { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; border: 1px solid transparent; margin-top: 14px; }
.hco-badge--trial { background: var(--h-violet-bg); color: var(--h-violet-text); border-color: var(--h-violet-border); }
.hco-badge__dot { width: 6px; height: 6px; border-radius: 50%; background: var(--h-violet); }

/* Result states */
.hco-result { max-width: 520px; margin: 8px auto 0; text-align: center; }
.hco-ico { width: 64px; height: 64px; border-radius: 50%; margin: 8px auto 20px; display: flex; align-items: center; justify-content: center; }
.hco-ico svg { width: 32px; height: 32px; stroke-width: 2.4; fill: none; stroke-linecap: round; stroke-linejoin: round; }
.hco-ico--ok { background: var(--h-success-bg); border: 1px solid var(--h-success-border); }
.hco-ico--ok svg { stroke: var(--h-success-text); }
.hco-ico--neutral { background: var(--h-neutral-bg); border: 1px solid var(--h-neutral-border); }
.hco-ico--neutral svg { stroke: var(--h-neutral-text); }
.hco-result h2 { font-size: 22px; font-weight: 600; letter-spacing: -.02em; margin: 0 0 8px; }
.hco-result p { color: var(--h-text-secondary); font-size: 14px; margin: 0 auto 6px; max-width: 44ch; }
.hco-receipt { text-align: left; margin: 24px 0; border: 1px solid var(--h-border); border-radius: var(--h-radius); overflow: hidden; }
.hco-r { display: flex; justify-content: space-between; gap: 12px; padding: 11px 16px; font-size: 13px; border-bottom: 1px solid var(--h-border); }
.hco-r:last-child { border-bottom: none; background: var(--h-bg-subtle); font-weight: 600; }
.hco-r__k { color: var(--h-text-secondary); }
.hco-mono { font-family: var(--h-font-mono); font-variant-numeric: tabular-nums; }
.hco-handoff { margin-top: 4px; font-size: 13px; }
.hco-btns { display: flex; gap: 12px; justify-content: center; margin-top: 18px; flex-wrap: wrap; }
.hco-btn { font: inherit; font-size: 14px; font-weight: 500; padding: 11px 18px; border-radius: var(--h-radius); border: 1px solid transparent; cursor: pointer; text-decoration: none; display: inline-flex; align-items: center; gap: 8px; }
.hco-btn--primary { background: var(--vendor-accent, var(--h-accent)); color: var(--vendor-on-accent, #fff); }
.hco-btn--primary:hover { background: var(--vendor-accent-hover, var(--h-accent-hover)); }
.hco-btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.hco-support { margin-top: 18px; font-size: 12.5px; color: var(--h-text-tertiary); }
.hco-support a { color: var(--h-accent-dark); }

/* Footer */
.hco-foot { max-width: 1000px; margin: 0 auto; padding: 0 24px 32px; color: var(--h-text-tertiary); font-size: 11.5px; display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; text-align: center; }
.hco-foot b { color: var(--h-text-secondary); }
.hco-foot__sep { opacity: .6; }

@media (max-width: 820px) {
  .hco-grid { grid-template-columns: 1fr; }
  .hco-aside { order: -1; }
}
@media (max-width: 460px) {
  .hco-wrap { padding: 20px 14px 40px; }
  .hco-pad { padding: 18px; }
  .hco-head__in { padding: 14px 16px; }
}
</style>
